// =====================================================================================
// INVRT Radar - web console - a contact's display name (story O8)
//
// The ONE home for the display-name fallbacks a full contact record carries: the
// person's own name when the record has one, else "Someone at <company>", else
// "Unnamed contact". Story C5 duplicated this in the ContactCard while the contacts
// module kept its own copy; O8 leaves one definition here and both surfaces import it.
// The untyped-payload cousin (personDisplayName on a raw provider record) stays in
// lib/format.ts - a different input shape, not a duplicate.
// =====================================================================================

import type { Contact } from "./types.c1";

/**
 * The slice of a contact record the name is read from. The C2 card object the wire
 * carries on GET /contacts/:id (ContactCard's ContactCardObject) is structurally this,
 * so a Contact, a card object or any record with the same person and company reads the
 * same way.
 */
type ContactIdentitySource = Pick<Contact, "person" | "company">;

/** The person's own name (full, or first + last joined), or null when the record has none. */
export function contactPersonName(contact: ContactIdentitySource): string | null {
  const full = contact.person.full_name?.trim() ?? "";
  if (full !== "") return full;
  const joined = [contact.person.first_name, contact.person.last_name]
    .map((part) => part?.trim() ?? "")
    .filter((part) => part !== "")
    .join(" ");
  return joined === "" ? null : joined;
}

/** The display name for a full record, with the honest fallbacks the drawer uses. */
export function contactDisplayName(contact: ContactIdentitySource): string {
  const person = contactPersonName(contact);
  if (person) return person;
  const company = contact.company.name?.trim() ?? "";
  if (company !== "") return `Someone at ${company}`;
  return "Unnamed contact";
}
